import type {
  CustomerStoriesBlock,
  PromoBandBlock,
  ScrollingTickerBlock,
  WeeklyHighlightsBlock,
} from "@/lib/storefront-types";

/* ------------------------------------------------------------------ */
/*  Static data for the curated / cleanup content blocks.             */
/*  Extracted so both original and improved versions share one source. */
/* ------------------------------------------------------------------ */

export type ProductCard = {
  name: string;
  price: string;
  summary: string;
  image?: string;
  objectPosition?: string;
};

export const stats = [
  { value: "2,400+", label: "Orders served" },
  { value: "4.9", label: "Average rating" },
  { value: "35 min", label: "Avg delivery time" },
  { value: "12", label: "London boroughs" },
] as const;

export const processSteps = [
  {
    number: "01",
    title: "Browse the menu",
    description:
      "Pick from rice meals, stews, bundles, or this week's special. Everything is priced and portioned clearly.",
    iconLabel: "fresh",
  },
  {
    number: "02",
    title: "We cook it fresh",
    description:
      "Every order is prepared in our kitchen using the same techniques and recipes you would find at a proper Lagos table.",
    iconLabel: "chef",
  },
  {
    number: "03",
    title: "Collect or get it delivered",
    description:
      "Pick up at your chosen time or have it brought to your door while it is still hot. You choose.",
    iconLabel: "delivery",
  },
] as const;

export const testimonials = [
  {
    quote:
      "Honestly the best Nigerian food I have had in London. The jollof has that smoky, party-rice flavour you cannot fake. I order for the whole office now.",
    name: "Folake D.",
    context: "Office catering regular",
  },
  {
    quote:
      "We got the family feast for my mum's birthday and she said it reminded her of home. That is the highest compliment you can get.",
    name: "Emeka T.",
    context: "Family order customer",
  },
  {
    quote:
      "Delivery was on time, food was piping hot, portions were huge. I have tried four other places and nothing comes close. This is the one.",
    name: "Yemi A.",
    context: "Delivery customer",
  },
] as const;

export const marqueeItems = [
  "Family feasts from GBP 49",
  "Same-day collection",
  "Office trays and party bundles",
  "Fresh jollof cooked daily",
  "Weekend hosting made easy",
  "London-wide delivery",
  "Bundle savings all week",
] as const;

export const serviceNotes = [
  {
    label: "Today",
    detail:
      "Collection slots open until 8:30 PM for smaller orders and family bundles.",
  },
  {
    label: "Thursday",
    detail:
      "Office lunch trays and repeat-workplace ordering take the spotlight.",
  },
  {
    label: "Friday",
    detail:
      "Bundle-heavy messaging works best when the weekend hosting mindset kicks in.",
  },
  {
    label: "Saturday",
    detail:
      "Use the page to push party platters, premium trays, and celebratory orders.",
  },
] as const;

export const demoTickerBlock: ScrollingTickerBlock = {
  kind: "scrollingTicker",
  appearance: "dark",
  tickerItems: marqueeItems.map((text) => ({ text })),
};

export const demoWeeklyHighlightsBlock: WeeklyHighlightsBlock = {
  kind: "weeklyHighlights",
  appearance: "dark",
  eyebrow: "This week at Aso Rock",
  headline:
    "Operational messaging gets a proper wide band instead of becoming tiny chips.",
  bodyHtml:
    "<p>This kind of section is useful when timing, availability, or the week's commercial focus should be easy to scan without looking like admin UI.</p>",
  highlightItems: serviceNotes.map((note) => ({
    label: note.label,
    detail: note.detail,
  })),
  buttons: [
    {
      enabled: true,
      label: "Browse this week's menu",
      href: "/menu",
      style: "primary",
      target: "self",
    },
  ],
};

export const demoCustomerStoriesBlock: CustomerStoriesBlock = {
  kind: "customerStories",
  appearance: "light",
  eyebrow: "Customer stories",
  headline: "Do not take our word for it.",
  storyItems: testimonials.map((testimonial) => ({
    quote: testimonial.quote,
    name: testimonial.name,
    context: testimonial.context,
  })),
};

export const demoPromoBandBlock: PromoBandBlock = {
  kind: "promoBand",
  appearance: "dark",
  eyebrow: "Second direction reset",
  headline:
    "This version is still wide, but now in the same family as the reference you liked.",
  bodyHtml:
    "<p>The rhythm is broader, band-led, and more like a commercial food landing page. The bundle remains the lead idea, but the whole page now sits inside a calmer wide-canvas system.</p>",
  notesPanelTitle: "Wide page notes",
  noteItems: [
    { text: "Broader bands with strong internal composition" },
    { text: "Bundle-first hero with clearer sales hierarchy" },
    { text: "Category, story, proof, and product moments" },
    { text: "Closer to the AsoRockFood campaign rhythm you pointed to" },
  ],
  buttons: [
    {
      enabled: true,
      label: "Back to the menu",
      href: "/menu",
      style: "primary",
      target: "self",
    },
  ],
};

export const runwayProducts: ProductCard[] = [
  {
    name: "Smoky Jollof Box",
    price: "GBP 12.00",
    summary:
      "The fast-conversion favourite for high-energy product storytelling.",
    image: "/home/hero-rice.png",
    objectPosition: "center 52%",
  },
  {
    name: "Ayamase Comfort Bowl",
    price: "GBP 14.50",
    summary:
      "Richer, slower, and better suited to premium or weekend-led sections.",
    image: "/home/hero-rice.png",
    objectPosition: "center 26%",
  },
  {
    name: "Family Rice Tray",
    price: "GBP 32.00",
    summary:
      "A reliable support product when the page needs to push sharing and upsell value.",
    image: "/home/hero-rice.png",
    objectPosition: "center 64%",
  },
  {
    name: "Chef's Bundle",
    price: "GBP 49.00",
    summary:
      "The stronger headline offer for this direction, designed to feel campaign-worthy.",
  },
];
